import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase, ROLE_LABEL, type Profile } from '@/lib/supabase'
import { useAuth } from '@/auth/useAuth'
import { invokeFunction } from '@/lib/invoke'
import { Badge, Button, Card, ErrorNote, PageHeader } from '@/components/ui'
import { InvitationActions } from '@/components/InvitationActions'

/** R2: named users under an organisation, so facilitators never share a login. */
export function TeamPage() {
  const { profile } = useAuth()
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState<string | null>(null)
  const [listError, setListError] = useState<string | null>(null)

  const { data: team } = useQuery({
    queryKey: ['team'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, org_id, role, full_name, email, is_active, auth_method')
        .order('full_name')
      if (error) throw error
      return data as Profile[]
    },
  })

  const { data: pending } = useQuery({
    queryKey: ['invitations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invitations')
        .select('id, email, role, created_at, expires_at')
        .is('accepted_at', null)
      if (error) throw error
      return data
    },
  })

  const invite = useMutation({
    mutationFn: async () => {
      await invokeFunction('invite-user', {
        email,
        fullName,
        orgId: profile?.org_id,
        role: 'facilitator',
      })
    },
    onSuccess: () => {
      setSent(email)
      setEmail('')
      setFullName('')
      setError(null)
      void queryClient.invalidateQueries({ queryKey: ['invitations'] })
    },
    onError: (e: Error) => setError(e.message || 'That invitation could not be sent.'),
  })

  // Auth creates the account (and handle_new_user its profile) when the
  // invitation is SENT, so an invitee has a profile row before they accept.
  // Show them once, as the pending invitation, until they do.
  const pendingEmails = new Set((pending ?? []).map((row) => row.email.toLowerCase()))
  const members = (team ?? []).filter((member) => !pendingEmails.has(member.email.toLowerCase()))

  function submit(event: FormEvent) {
    event.preventDefault()
    invite.mutate()
  }

  return (
    <>
      <PageHeader title="Your team">
        Each facilitator gets their own login. That keeps access accurate when someone joins or
        leaves, and lets you see who is delivering.
      </PageHeader>

      <Card className="mb-6 max-w-xl">
        <h2 className="text-base text-ink">Invite a facilitator</h2>
        <form onSubmit={submit} className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium text-ink">Full name</span>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="mt-1 w-full rounded-sm border border-line px-3 py-2"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-ink">Email address</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded-sm border border-line px-3 py-2"
            />
          </label>
          <div className="sm:col-span-2">
            {error && <div className="mb-3"><ErrorNote>{error}</ErrorNote></div>}
            {sent && <p className="mb-3 text-sm text-pounamu">Invitation sent to {sent}.</p>}
            <Button type="submit" disabled={invite.isPending}>
              {invite.isPending ? 'Sending…' : 'Send invitation'}
            </Button>
          </div>
        </form>
      </Card>

      <h2 className="mb-3 text-base text-ink">People with access</h2>
      {listError && <div className="mb-3"><ErrorNote>{listError}</ErrorNote></div>}
      <div className="overflow-x-auto rounded-md border border-line bg-surface">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-inksoft">
            <tr>
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Email</th>
              <th className="px-4 py-2 font-medium">Role</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.id} className="border-b border-line last:border-0">
                <td className="px-4 py-2">{member.full_name ?? '—'}</td>
                <td className="px-4 py-2 text-inksoft">{member.email}</td>
                <td className="px-4 py-2">{ROLE_LABEL[member.role]}</td>
                <td className="px-4 py-2">
                  <Badge tone={member.is_active ? 'good' : 'neutral'}>
                    {member.is_active ? 'Active' : 'Not active'}
                  </Badge>
                </td>
                <td className="px-4 py-2" />
              </tr>
            ))}
            {(pending ?? []).map((row) => (
              <tr key={row.id} className="border-b border-line last:border-0">
                <td className="px-4 py-2 text-inksoft">Invitation sent</td>
                <td className="px-4 py-2 text-inksoft">{row.email}</td>
                <td className="px-4 py-2 text-inksoft">Facilitator</td>
                <td className="px-4 py-2"><Badge>Awaiting first sign-in</Badge></td>
                <td className="px-4 py-2 text-right">
                  <InvitationActions
                    invitation={row}
                    onChanged={() => {
                      setListError(null)
                      void queryClient.invalidateQueries({ queryKey: ['invitations'] })
                      void queryClient.invalidateQueries({ queryKey: ['team'] })
                    }}
                    onError={setListError}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}
